import React, { useState, useEffect } from "react";
import NewChat from "./NewChat";
import axios from "axios";
import localforage from "localforage";
import "../styles/chatlist.css";
import Logout from "./Logout";
import { useSelector } from "react-redux";
import { faSyncAlt } from "@fortawesome/free-solid-svg-icons";
import LoadingSpinner from "../../shared/helper/LoadingSpinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import { refreshFcm } from "../../auth/store/authSlice";
import { useDispatch } from "react-redux";

const ChatList = ({ onSelectChat, title }) => {
  const dispatch = useDispatch();
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const apiUrl = process.env.REACT_APP_API_URL;
  const { accessToken,user } = useSelector((state) => state?.auth);

  const CACHE_KEY = "chatListCache";
  const CACHE_TIMESTAMP_KEY = "chatListCacheTimestamp";

  const loadFromCache = async () => {
    const cachedChats = await localforage.getItem(CACHE_KEY);
    if (cachedChats && Array.isArray(cachedChats)) {
      const sortedChats = cachedChats.sort((a, b) =>
        new Date(b.last_message_time).getTime() - new Date(a.last_message_time).getTime()
      );
      setChats(sortedChats);
    }
    setLoading(false);
  };

  const isCacheStale = async (maxAgeInMinutes = 5) => {
    const timestamp = await localforage.getItem(CACHE_TIMESTAMP_KEY);
    if (!timestamp) return true;
    const now = new Date();
    const cachedTime = new Date(timestamp);
    const diff = (now - cachedTime) / (1000 * 60);
    return diff > maxAgeInMinutes;
  };

  const fetchChats = async (since = null) => {
    try {
      const response = await axios.get(apiUrl + "whatsapp/chat-list", {
        params: since ? { since } : {},
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      const newChats = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.data)
          ? response.data.data
          : [];
      console.log("New chats after fetch: ", newChats);
      const cachedChats = await localforage.getItem(CACHE_KEY) || [];
      console.log("Cached chats before merge:", cachedChats);

      const chatMap = new Map();
      cachedChats.forEach(chat => {
        chatMap.set(chat.phone_number, chat);
      });

      newChats.forEach(chat => {
        const old = chatMap.get(chat.phone_number);
        const newTime = new Date(chat.last_message_time).getTime();
        const oldTime = old ? new Date(old.last_message_time).getTime() : 0;

        if (!old || newTime > oldTime) {
          chatMap.set(chat.phone_number, chat);
        }
      });

      const mergedChats = Array.from(chatMap.values()).sort((a, b) =>
        new Date(b.last_message_time).getTime() - new Date(a.last_message_time).getTime()
      );
      console.log("Merged chats: ", mergedChats);

      await localforage.setItem(CACHE_KEY, mergedChats);
      await localforage.setItem(CACHE_TIMESTAMP_KEY, new Date().toISOString());

      return mergedChats;
    } catch (error) {
      console.error("Error fetching chats:", error);
      return null;
    }
  };

  const refreshMessages = async (forceRefresh = false) => {
    console.log("start refrest");
    setIsRefreshing(true);
    setLoading(true);

    const isStale = await isCacheStale(5);
    if (forceRefresh || isStale) {
      const cachedChats = await localforage.getItem(CACHE_KEY) || [];
      const lastUpdatedAt = getLastUpdatedAtFromChats(cachedChats);
      const mergedChats = await fetchChats(lastUpdatedAt);
      if (mergedChats) {
        setChats([...mergedChats]); // force re-render with new array
      }
    } else {
      const cachedChats = await localforage.getItem(CACHE_KEY);
      if (cachedChats) {
        const sortedChats = cachedChats.sort((a, b) =>
          new Date(b.last_message_time).getTime() - new Date(a.last_message_time).getTime()
        );
        setChats(sortedChats);
      }
    }

    setIsRefreshing(false);
    setLoading(false);
    //clear notification
    if (window.FcmTokenChannel) {
      window.FcmTokenChannel.postMessage("clearNotification");
    }
  };

  const getLastUpdatedAtFromChats = (chatList) => {
    if (!chatList || chatList.length === 0) return null;
    const latestChat = chatList.reduce((latest, chat) => {
      const current = new Date(chat.last_message_time).getTime();
      const previous = new Date(latest.last_message_time).getTime();
      return current > previous ? chat : latest;
    });
    return new Date(latestChat.last_message_time).toISOString();
  };

  const handleNewChat = (chat) => {
    const newChat = {
      phone_number: chat.phoneNumber,
      name: "Client",
      last_message: chat.message,
      last_message_time: new Date().toISOString(),
    };
    const updatedChats = [...chats.filter(c => c.phone_number !== chat.phoneNumber), newChat];
    const sortedChats = updatedChats.sort((a, b) =>
      new Date(b.last_message_time).getTime() - new Date(a.last_message_time).getTime()
    );
    setChats(sortedChats);
    localforage.setItem(CACHE_KEY, sortedChats);
  };

  useEffect(() => {
    const loadAndRefresh = async () => {
      await loadFromCache();
      await refreshMessages(true);
    };
    loadAndRefresh();
  }, []);

   // Receive notification from FCM
   window.receiveMessage = function () {
    console.log("Refreshing due to push notification")
    refreshMessages(true); // trigger refresh when notification hits
  };

  //Refresh FCM token
  window.refreshFCMToken = function (token) {
    console.log("FCM refresh token", token)
    const phone = user.phone;
    const fcm_token = token;
    dispatch(refreshFcm({phone,fcm_token}))
   
  };  

  const filteredChats = chats.filter((chat) => {
    const query = searchQuery.toLowerCase();
    return (
      chat?.name?.toLowerCase().includes(query) ||
      chat?.phone_number?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="chat-sidebar">
      <div className="d-flex justify-content-between align-items-center px-2 py-2">
        <NewChat onNewChat={handleNewChat} title={title} />
        <div style={{ position: "relative", display: "inline-block" }}>
          <div
            className="new-logout-btn"
            style={{
              position: "fixed",
              bottom: "20px",
              right: "20px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "12px",
              marginBottom: "100px",
              zIndex: 9999,
            }}
          >
            {isRefreshing ? (
              <LoadingSpinner />
            ) : (
              <FontAwesomeIcon
                icon={faSyncAlt}
                onClick={() => refreshMessages(true)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "40px",
                  color: "#075E54",
                  padding: "10px",
                }}
                title="Refresh"
              />
            )}
            <Logout />
          </div>
        </div>
      </div>
      <div className="chat-search-box px-2 pb-2"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 2,
        }}
      >
        <input
          type="text"
          name="chat-list-search"
          placeholder="Search by name or phone..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="form-control"
        />
      </div>
      <div className="chat-list">
        {loading || isRefreshing ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "80vh" }}
          >
            <LoadingSpinner />
          </div>
        ) : filteredChats?.length > 0 ? (
          filteredChats.map((chat) => (
            <div
              key={chat?.phone_number}
              className="chat-item"
              onClick={() => onSelectChat(chat)}
            >
              <div className="chat-header">
                <h6>{chat?.name}</h6>
              </div>
              <span
                className="chat-phone-number"
                style={{ fontSize: "14px", color: "#a3a3a3" }}
              >
                {chat?.phone_number}
              </span>
              <p className="chat-latest-message">{chat?.last_message}</p>
              <div className="chat-meta" style={{ fontSize: "12px", color: "#999" }}>
                {moment(chat.last_message_time).format("DD/MM/YY hh:mm A")}
              </div>
            </div>
          ))
        ) : (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "93vh", textAlign: "center", color: "#666" }}
          >
            <h5>No chats available</h5>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatList;
